import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { systemApi } from '../api/system';
import { watchMaintenance } from '../services/maintenance';
import { useAccess } from './AccessContext';

const KEY = 'amposter:maintenance:v1';
const DEFAULT = { enabled: false, message: '', revision: 0 };
const Context = createContext(null);
export function readMaintenanceSnapshot() {
  try { const value = JSON.parse(localStorage.getItem(KEY)); return typeof value?.enabled === 'boolean' ? value : DEFAULT; }
  catch { return DEFAULT; }
}
export const useMaintenance = () => useContext(Context);
export function MaintenanceProvider({ children }) {
  const access = useAccess(), accountOwner = access.accountOwner ?? access.owner;
  const canManageMaintenance = access.accountCanManageMaintenance ?? (accountOwner || Boolean(access.hasPermission?.('MAINTENANCE_MANAGE')));
  const [state, setState] = useState(readMaintenanceSnapshot), [checking, setChecking] = useState(true);
  const latest = useRef(state);
  const [online, setOnline] = useState(navigator.onLine !== false), [error, setError] = useState(false);
  const apply = useCallback(next => {
    if (typeof next?.enabled !== 'boolean') return null;
    const current = latest.current;
    if (Number(next.revision) >= Number(current.revision) || next.revision == null) {
      const value = { enabled: next.enabled, message: String(next.message || ''), revision: next.revision ?? current.revision };
      // Public status only: never cache owner access or other permissions.
      try { localStorage.setItem(KEY, JSON.stringify(value)); } catch {}
      latest.current = value;
      setState(value);
    }
    setChecking(false); setError(false);
    return latest.current;
  }, []);
  useEffect(() => watchMaintenance(apply, () => { setChecking(false); setError(true); }), [apply]);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine !== false);
    window.addEventListener('online', update); window.addEventListener('offline', update);
    return () => { window.removeEventListener('online', update); window.removeEventListener('offline', update); };
  }, []);
  const refresh = async () => {
    setChecking(true);
    try {
      const next = await systemApi.maintenance();
      if (typeof next?.enabled !== 'boolean') throw new Error('Invalid maintenance status');
      return apply(next);
    }
    catch { setChecking(false); setError(true); return null; }
  };
  return <Context.Provider value={{ state, checking, online, error, refresh, apply, accountOwner, canManageMaintenance,
    blocked: state.enabled && !accountOwner, accessLoading: access.loading }}>{children}</Context.Provider>;
}
